import { datadogLogs, LogsInitConfiguration } from "@datadog/browser-logs";
import { datadogRum, DefaultPrivacyLevel, RumInitConfiguration } from "@datadog/browser-rum";
import posthog from "posthog-js";
import { getGraphQLBaseUrl, getStage } from "src/context";

/* Since Stage is meaningful in the GraphQL context as well, grab off the ReturnType vs. exporting from context.ts */
type Stage = ReturnType<typeof getStage>;

/** Enable third-party analytics tools */
export async function enableAnalytics(stage = getStage()) {
  if (stage === "prod") {
    enablePosthog();
  }
  if (stage !== "local" && stage !== "local-prod") {
    enableDatadog(stage);
    datadogRum.startSessionReplayRecording();
  }
}

export function setAnalyticsUser(user: { id?: string; email?: string; name?: string }, isInternal: boolean): void {
  const { id, email, name } = user;
  const userType = isInternal ? "internal" : "homeowner";
  // Attach user identity to PostHog
  posthog.identify(`${userType}_users_${id}`, { email, name });

  // Identify user with Datadog Real User Monitoring
  // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#identify-user-sessions
  if (id) {
    datadogRum.setUser({ id, name, email, userType });
    datadogLogs.setUser({ id, name, email, userType });
  } else {
    datadogRum.clearUser();
    // There isn't a datadogLogs.removeUser, so remove each key
    ["id", "name", "email", "userType"].forEach((key) => datadogLogs.removeUserProperty(key));
  }
}

const DATADOG_IGNORED_ERROR_MESSAGES = /ResizeObserver loop/;
const THIRD_PARTY_ERROR_SOURCE = /posthog/i;

/** Returns true if the error originated from a third-party script like PostHog */
export function isThirdPartyError(error?: { stack?: string; message?: string }): boolean {
  return THIRD_PARTY_ERROR_SOURCE.test(error?.stack ?? "") || THIRD_PARTY_ERROR_SOURCE.test(error?.message ?? "");
}

function enableDatadog(stage: Stage): void {
  // Configure Datadog RUM
  // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
  const commonOps: Omit<RumInitConfiguration, "applicationId"> & LogsInitConfiguration = {
    clientToken: "pub5d56542aed28b62c91444885b219da73",
    site: "datadoghq.com",
    service: "plan-configuration-frontend",
    env: stage,
    version: VITE_GIT_COMMIT ?? "local",
    // Our tests call init multiple times to test our setup logic
    silentMultipleInit: true,
  };

  // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
  datadogRum.init({
    ...commonOps,
    applicationId: "842de650-6245-416a-b86d-b0716dafc7d3",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [getGraphQLBaseUrl()],
    // https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/data_collected/#graphql-attributes
    allowedGraphQlUrls: [/\/graphql$/],
    beforeSend: (event) => {
      // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#enrich-and-control-rum-data
      if (event.type === "error" && DATADOG_IGNORED_ERROR_MESSAGES.test(event.error.message)) {
        return false;
      }
      if (event.type === "error" && isThirdPartyError(event.error)) {
        return false;
      }
      return true;
    },
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
    // Enabling the beta HeatMap feature
    enableExperimentalFeatures: ["clickmap"],
  });

  datadogLogs.init({
    ...commonOps,
    forwardConsoleLogs: [
      // Remember that we pay per-log ingested. You should likely avoid reporting debug-level log messages
      // unless you're actively debugging a major, global issue.
      // "debug",
      "info",
      "warn",
      "error",
    ],
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: (event) => {
      // Abort errors are not necessarily error in the app. They are typically triggered by the user navigating away from
      // a page and causing an in-flight request to be canceled. We don't want to report these as errors.
      if (event.error && event.error.stack?.startsWith("AbortError:")) {
        return false;
      }
      if (isThirdPartyError(event.error)) {
        return false;
      }
      return !DATADOG_IGNORED_ERROR_MESSAGES.test(event.message);
    },
  });
}

function enablePosthog() {
  posthog.init("phc_813WZxVPPPSHtUKAmrG7PNdXHXWvkPyexWzcsJV5KEk", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
  });
}

// Track the currently active save vital so stopSaveVital knows what to stop
let currentSaveVital = "";

/** Start tracking a save duration vital (optionPricing or planChange). */
export function startSaveVital(isPlanChange: boolean = false): void {
  currentSaveVital = isPlanChange ? "planChange" : "optionPricing";
  datadogRum.startDurationVital(currentSaveVital);
}

/** Stop tracking the current save duration vital. */
export function stopSaveVital(): void {
  if (currentSaveVital) {
    datadogRum.stopDurationVital(currentSaveVital);
    currentSaveVital = "";
  }
}
